/**
 * useTransliterate — headless hook that gives any controlled <input>
 * or <textarea> the same "type Latin → Space converts to Indic script"
 * behaviour that <TransliterateInput /> ships. Also drives the TipTap
 * extension consumed by <PublisherEditor />.
 *
 * The consumer keeps full control of styling; the hook only supplies:
 *   • onKeyDown       → attach to the input
 *   • LanguageChip    → a ready-to-render picker (positioned by caller)
 *   • lang, setLang   → so the caller can display state
 *
 * Usage:
 *   const t = useTransliterate({ value, onChange, inputRef, preferredLang: activeLang });
 *   return (
 *     <div className="relative">
 *       <input ref={inputRef} value={value} onChange={...} onKeyDown={t.onKeyDown} />
 *       <div className="absolute right-2 top-2">{t.chip}</div>
 *     </div>
 *   );
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Languages, ChevronDown, Loader2 } from "lucide-react";

const I18N_BASE = `${process.env.REACT_APP_BACKEND_URL || ""}/api/i18n`;

export const TRANSLIT_LANGS = [
  { code: "off", label: "Off",       native: "English",  short: "EN" },
  { code: "hi",  label: "Hindi",     native: "हिन्दी",    short: "HI" },
  { code: "ml",  label: "Malayalam", native: "മലയാളം",  short: "ML" },
  { code: "ta",  label: "Tamil",     native: "தமிழ்",    short: "TA" },
  { code: "te",  label: "Telugu",    native: "తెలుగు",   short: "TE" },
  { code: "kn",  label: "Kannada",   native: "ಕನ್ನಡ",    short: "KN" },
  { code: "bn",  label: "Bengali",   native: "বাংলা",    short: "BN" },
  { code: "mr",  label: "Marathi",   native: "मराठी",    short: "MR" },
  { code: "gu",  label: "Gujarati",  native: "ગુજરાતી",  short: "GU" },
  { code: "pa",  label: "Punjabi",   native: "ਪੰਜਾਬੀ",   short: "PA" },
  { code: "or",  label: "Odia",      native: "ଓଡ଼ିଆ",    short: "OR" },
  { code: "as",  label: "Assamese",  native: "অসমীয়া",  short: "AS" },
  { code: "ur",  label: "Urdu",      native: "اردو",     short: "UR" },
];
export const TRANSLIT_PREF_KEY = "sp:input-lang";
export const TRANSLIT_SUPPORTED_CODES = new Set(TRANSLIT_LANGS.map((l) => l.code));
const TRIGGER_KEYS = new Set([" ", "Enter", ".", ",", ";", ":", "!", "?"]);

async function callTransliterate(text, targetLang, authToken) {
  try {
    const r = await fetch(`${I18N_BASE}/transliterate`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
      },
      body: JSON.stringify({ text, target_lang: targetLang }),
    });
    if (!r.ok) return text;
    const data = await r.json();
    return data?.transliterated || text;
  } catch (_e) {
    return text;
  }
}

/**
 * @param {object} opts
 * @param {string} opts.value                current text value
 * @param {(v:string)=>void} opts.onChange   controlled setter
 * @param {{current: HTMLInputElement|HTMLTextAreaElement|null}} opts.inputRef
 * @param {string=} opts.preferredLang       parent-provided default (e.g. active translation tab)
 * @param {string=} opts.authToken           optional JWT for auth-scoped calls
 * @param {string=} opts.testIdPrefix        prefix for chip data-testids
 */
export function useTransliterate({
  value,
  onChange,
  inputRef,
  preferredLang = null,
  authToken = null,
  testIdPrefix = "translit",
}) {
  const [lang, setLang] = useState(() => {
    if (preferredLang && TRANSLIT_SUPPORTED_CODES.has(preferredLang)) return preferredLang;
    if (typeof window !== "undefined") {
      const saved = window.localStorage.getItem(TRANSLIT_PREF_KEY);
      if (saved && TRANSLIT_SUPPORTED_CODES.has(saved)) return saved;
    }
    return "off";
  });
  const [busy, setBusy] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const langRef = useRef(lang);

  useEffect(() => {
    langRef.current = lang;
    if (typeof window !== "undefined" && lang) {
      window.localStorage.setItem(TRANSLIT_PREF_KEY, lang);
    }
  }, [lang]);

  // Follow the parent's explicit choice. Non-Indic languages (English,
  // Spanish, French, Arabic …) reset to OFF so Latin words on those
  // tabs never get accidentally converted.
  useEffect(() => {
    if (!preferredLang) return;
    if (TRANSLIT_SUPPORTED_CODES.has(preferredLang)) setLang(preferredLang);
    else setLang("off");
  }, [preferredLang]);

  const meta = useMemo(
    () => TRANSLIT_LANGS.find((l) => l.code === lang) || TRANSLIT_LANGS[0],
    [lang],
  );

  // Convert the last word before the caret. Fires on trigger keys.
  // NOTE: We read the live DOM value + caret via inputRef instead of
  // the closure-captured `value` because React's controlled onChange
  // fires AFTER the browser inserts the character, so at setTimeout(0)
  // the state hasn't rerendered yet even though the DOM has the space.
  const convertLastWord = useCallback(async () => {
    const activeLang = langRef.current;
    if (!activeLang || activeLang === "off") return;
    const el = inputRef?.current;
    if (!el) return;
    const liveValue = el.value ?? "";
    const pos = el.selectionStart ?? liveValue.length;
    const upto = liveValue.slice(0, pos);
    const match = upto.match(/(\S+?)([\s.,;:!?]+)$/);
    if (!match) return;
    const word = match[1];
    const trailing = match[2].length;
    if (!word || word.length < 2) return;
    if (/[^\x00-\x7F]/.test(word)) return;
    if (/^(https?:\/\/|www\.|[@#])/i.test(word)) return;
    if (word.includes("@") || word.includes("/")) return;

    setBusy(true);
    const converted = await callTransliterate(word, activeLang, authToken);
    setBusy(false);
    if (!converted || converted === word) return;
    // Re-read the DOM in case the writer kept typing while we awaited.
    const latest = inputRef?.current?.value ?? liveValue;
    const wordEnd = pos - trailing;
    const wordStart = wordEnd - word.length;
    if (wordStart < 0) return;
    if (latest.slice(wordStart, wordEnd) !== word) return;
    const newValue = latest.slice(0, wordStart) + converted + latest.slice(wordEnd);
    onChange(newValue);
    // Restore caret after the converted word + preserved trailing.
    const newCaret = wordStart + converted.length + trailing;
    setTimeout(() => {
      if (inputRef?.current) {
        try {
          inputRef.current.selectionStart = newCaret;
          inputRef.current.selectionEnd = newCaret;
        } catch (_e) { /* selection API not supported on some inputs */ }
      }
    }, 0);
  }, [onChange, inputRef, authToken]);

  const onKeyDown = useCallback((e) => {
    if (!TRIGGER_KEYS.has(e.key)) return;
    if (langRef.current === "off") return;
    // Let the character insert first, then convert the just-terminated word.
    setTimeout(() => { convertLastWord(); }, 0);
  }, [convertLastWord]);

  const chip = (
    <div className="relative flex items-center gap-1" data-testid={`${testIdPrefix}-chipwrap`}>
      {busy && (
        <Loader2 className="w-3 h-3 animate-spin text-slate-400" data-testid={`${testIdPrefix}-busy`} />
      )}
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); setPickerOpen((v) => !v); }}
        className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest shadow-sm hover:bg-slate-50"
        style={{ color: lang === "off" ? "#6B7280" : "#B58936" }}
        title={lang === "off"
          ? "Type in English (no live conversion). Click to enable Indic script."
          : `Live-converting to ${meta.native}. Click to change.`}
        data-testid={`${testIdPrefix}-chip`}
      >
        <Languages className="w-3 h-3" />
        {meta.short}
        <ChevronDown className="w-2.5 h-2.5" />
      </button>
      {pickerOpen && (
        <div
          className="absolute top-full right-0 mt-1 rounded-lg border border-slate-200 bg-white shadow-lg py-1 max-h-72 overflow-auto z-30"
          style={{ minWidth: 210 }}
          data-testid={`${testIdPrefix}-picker`}
        >
          {TRANSLIT_LANGS.map((l) => (
            <button
              key={l.code}
              type="button"
              onClick={() => { setLang(l.code); setPickerOpen(false); }}
              className={`block w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50 ${lang === l.code ? "font-semibold" : ""}`}
              data-testid={`${testIdPrefix}-picker-${l.code}`}
            >
              <span className="opacity-60 mr-1.5 text-[10px] uppercase tracking-widest">{l.short}</span>
              <span className="mr-1.5">{l.native}</span>
              {l.code !== "off" && <span className="opacity-40 text-[10px]">· {l.label}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  return { lang, setLang, onKeyDown, busy, chip };
}

export default useTransliterate;
